import { DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import {
  CustomerApprovalEventBounced,
  CustomerApprovalEventClicked,
  CustomerApprovalEventDeferred,
  CustomerApprovalEventDelivered,
  CustomerApprovalEventDropped,
  CustomerApprovalEventOpened,
  CustomerApprovalEventProcessing,
  CustomerApprovalEventSending,
  CustomerApprovalEventSpamReport,
  CustomerApprovalEventUnsubscribed,
  CustomerApprovalEventUpdatedAnswers,
  DeclineCancellationEvent,
  RequestCancellationEvent,
  UserInterface as SalesUser,
  Status,
  UpdateContractDurationEvent,
  UpdatedCustomerNotesEvent,
  UpdatedDraftEvent,
  UpdatedLineItemsEvent,
  UpdatedNotesEvent,
  UpdatedRequestedActivationEvent,
  UpdatedTagsEvent,
  UpdateEnforceContractTermEvent,
} from '@vendasta/sales-orders';
import { OrderDetailField } from '@vendasta/sales-ui';

@Injectable()
export class HistoryService {
  getEventNameTranslationKeyForOrderDetailField(orderDetailField: OrderDetailField): string {
    if (!orderDetailField || !orderDetailField.metadata) {
      return null;
    }

    if (orderDetailField.metadata.event.id === UpdatedTagsEvent) {
      return 'FRONTEND.SALES_UI.UPDATED_TAGS';
    }

    if (orderDetailField.metadata.event.id === UpdatedNotesEvent) {
      return 'FRONTEND.SALES_UI.UPDATED_NOTES';
    }

    if (orderDetailField.metadata.event.id === UpdatedCustomerNotesEvent) {
      return 'FRONTEND.SALES_UI.UPDATED_CUSTOMER_NOTES';
    }

    if (orderDetailField.metadata.event.id === UpdatedRequestedActivationEvent) {
      return 'FRONTEND.SALES_UI.UPDATED_CONTRACT_START';
    }

    if (orderDetailField.metadata.event.id === UpdateContractDurationEvent) {
      return 'FRONTEND.SALES_UI.UPDATED_CONTRACT_LENGTH';
    }

    if (orderDetailField.metadata.event.id === UpdateEnforceContractTermEvent) {
      return 'FRONTEND.SALES_UI.UPDATED_ENFORCE_CONTRACT_TERM';
    }

    if (orderDetailField.metadata.event.id === RequestCancellationEvent) {
      return 'FRONTEND.SALES_UI.CANCELLATION_REQUESTED';
    }

    if (orderDetailField.metadata.event.id === DeclineCancellationEvent) {
      return 'FRONTEND.SALES_UI.CANCELLATION_DECLINED';
    }

    if (orderDetailField.metadata.event.id === UpdatedDraftEvent) {
      return 'FRONTEND.SALES_UI.UPDATED_DRAFT';
    }

    if (orderDetailField.metadata.event.id === UpdatedLineItemsEvent) {
      return 'FRONTEND.SALES_UI.UPDATED_CONTENTS';
    }

    if (orderDetailField.status !== Status.SUBMITTED_FOR_CUSTOMER_APPROVAL) {
      return null;
    }
    return this.getCustomerApprovalTranslationKeyForEventID(orderDetailField.metadata.event.id);
  }

  formatDate(date: Date): string {
    return new DatePipe('en-US').transform(date, 'MMMM d, y');
  }

  formatDateWithTime(date: Date): string {
    return new DatePipe('en-US').transform(date, 'MMMM d, y, h:mm:ss a');
  }

  getDisplayNameForSalesUser(user: SalesUser): string {
    if (!user) {
      return '';
    }
    const names = [];
    if (user.firstName) {
      names.push(user.firstName);
    }
    if (user.lastName) {
      names.push(user.lastName);
    }
    const displayName = names.join(' ');
    if (!displayName) {
      return user.email;
    }
    return displayName;
  }

  getCustomerApprovalTranslationKeyForEventID(eventID: string): string {
    switch (eventID) {
      case CustomerApprovalEventSending:
        return 'FRONTEND.SALES_UI.CUSTOMER_EVENTS.SENDING';
      case CustomerApprovalEventProcessing:
        return 'FRONTEND.SALES_UI.CUSTOMER_EVENTS.PROCESSING';
      case CustomerApprovalEventDelivered:
        return 'FRONTEND.SALES_UI.CUSTOMER_EVENTS.DELIVERED';
      case CustomerApprovalEventBounced:
        return 'FRONTEND.SALES_UI.CUSTOMER_EVENTS.BOUNCED';
      case CustomerApprovalEventDropped:
        return 'FRONTEND.SALES_UI.CUSTOMER_EVENTS.DROPPED';
      case CustomerApprovalEventUnsubscribed:
        return 'FRONTEND.SALES_UI.CUSTOMER_EVENTS.UNSUBSCRIBED';
      case CustomerApprovalEventDeferred:
        return 'FRONTEND.SALES_UI.CUSTOMER_EVENTS.DEFERRED';
      case CustomerApprovalEventSpamReport:
        return 'FRONTEND.SALES_UI.CUSTOMER_EVENTS.SPAM_REPORT';
      case CustomerApprovalEventOpened:
        return 'FRONTEND.SALES_UI.CUSTOMER_EVENTS.EMAIL_OPENED';
      case CustomerApprovalEventClicked:
        return 'FRONTEND.SALES_UI.CUSTOMER_EVENTS.EMAIL_CLICKED';
      case CustomerApprovalEventUpdatedAnswers:
        return 'FRONTEND.SALES_UI.CUSTOMER_EVENTS.UPDATED_ANSWERS';
      default:
        return 'FRONTEND.SALES_UI.CUSTOMER_EVENTS.UNKNOWN';
    }
  }
}
